import { type Course, UserAction } from "@brookbush/types";

import clsx from "clsx";
import { GiftIcon, SparklesIcon } from "lucide-react";
import posthog from "posthog-js";
import { useEffect } from "react";

import Button from "~/components/Button";
import { useUserAction } from "~/hooks";
import useFreeCourse from "~/hooks/useFreeCourse";
import useTrackCTA from "~/hooks/useTrackCTA";
import { getFreeCourseCopy } from "~/lib/free-course";

interface CourseFreeCourseCardProps {
	course: Course;
}

/** Free-course offer, claimed note, or "already used" paywall for one course. */
const CourseFreeCourseCard = ({ course }: CourseFreeCourseCardProps) => {
	const { state, openClaimIfOffered } = useFreeCourse(course._id);
	const { setUserAction } = useUserAction();
	const trackCTA = useTrackCTA();
	const copy = getFreeCourseCopy(state);

	useEffect(() => {
		if (state.kind === "offer" || state.kind === "used") {
			posthog.capture(
				state.kind === "offer"
					? "free_course:offer_shown"
					: "free_course:paywall_shown",
				{ course_id: course._id },
			);
		}
	}, [state.kind, course._id]);

	if (!copy) return null;

	return (
		<div
			data-testid={`free-course-${state.kind}`}
			className={clsx(
				"flex flex-col gap-3 rounded-xl border bg-white px-4 py-3 shadow shadow-slate-100 dark:bg-slate-800 dark:shadow-slate-900",
				{
					"border-emerald-300 dark:border-emerald-700": state.kind !== "used",
				},
			)}
		>
			{copy.title && (
				<p className="flex items-center gap-2 font-semibold text-lg">
					<GiftIcon size={20} className="text-emerald-600" />
					{copy.title}
				</p>
			)}

			<p className="flex gap-2 text-slate-700 dark:text-slate-200">
				{state.kind === "claimed" && (
					<SparklesIcon
						size={20}
						className="mt-0.5 shrink-0 text-emerald-600"
					/>
				)}
				{copy.body}
			</p>

			{copy.button && (
				<Button
					className="w-full"
					onClick={() => {
						if (state.kind === "offer") {
							openClaimIfOffered();
							return;
						}

						posthog.capture("free_course:paywall_clicked", {
							course_id: course._id,
						});
						trackCTA(UserAction.BECOME_A_MEMBER, "Free Course Used Card");
						setUserAction(UserAction.BECOME_A_MEMBER);
					}}
				>
					{copy.button}
				</Button>
			)}
		</div>
	);
};

export default CourseFreeCourseCard;
